/**
 * PlotlyChart component - Generic Plotly visualization wrapper
 *
 * Features:
 * - Fetches visualization from backend URL (HTML with embedded Plotly)
 * - Loading and error state handling
 * - Space theme styling
 * - Interactive pan/zoom
 * - Full-size view option via iframe
 */

import { useState, useEffect } from 'react'
import { Loader2, AlertCircle, Maximize2 } from 'lucide-react'
import { fetchWithTimeout } from '../lib/http'

interface PlotlyChartProps {
  /** URL to fetch the Plotly HTML from */
  dataUrl: string
  /** Title of the visualization */
  title: string
  /** Whether to show in full-size mode */
  fullSize?: boolean
}

function PlotlyChart({ dataUrl, title, fullSize = false }: PlotlyChartProps) {
  const [htmlContent, setHtmlContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    const fetchPlot = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetchWithTimeout(dataUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch plot: ${response.status} ${response.statusText}`)
        }

        const html = await response.text()
        setHtmlContent(html)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load visualization')
        console.error('PlotlyChart fetch error:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchPlot()
  }, [dataUrl])

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen)
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-dark-50 rounded-lg border border-dark-200">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-space-400 mx-auto mb-2" />
          <p className="text-sm text-dark-500">Loading visualization...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg border border-red-200">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700 font-medium">Error loading visualization</p>
          <p className="text-xs text-red-600 mt-1">{error}</p>
        </div>
      </div>
    )
  }

  // Full screen modal overlay
  if (isFullScreen && htmlContent) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-80 z-50 flex flex-col"
        onClick={toggleFullScreen}
      >
        <div className="flex items-center justify-between p-4 bg-dark-900 text-white">
          <h3 className="text-lg font-medium">{title}</h3>
          <button
            onClick={toggleFullScreen}
            className="px-4 py-2 bg-space-500 hover:bg-space-600 rounded transition-colors"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            srcDoc={htmlContent}
            title={title}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>
    )
  }

  // Normal view
  return (
    <div className="relative group">
      {/* Full-size button */}
      {fullSize && (
        <button
          onClick={toggleFullScreen}
          className="absolute top-2 right-2 z-10 p-2 bg-dark-900 bg-opacity-70 hover:bg-opacity-90 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"
          title="View full screen"
        >
          <Maximize2 size={16} />
        </button>
      )}

      {/* Plotly iframe */}
      {htmlContent && (
        <iframe
          srcDoc={htmlContent}
          title={title}
          className={`w-full border-0 rounded-lg ${fullSize ? 'h-96' : 'h-64'}`}
          sandbox="allow-scripts allow-same-origin"
        />
      )}
    </div>
  )
}

export default PlotlyChart
